/**
 * Homestead beacon markers — a lit ward post + a ground ring at the claim radius,
 * drawn at each beacon's planet-local anchor. Each marker is parented to its
 * planet's `group`, so it rides the planet's orbit + spin for free (the
 * resourceNodes.ts precedent) — no per-frame repositioning.
 *
 * Fed by the owner-only `hsState` snapshot (setBeacons) and live `hsDelta`
 * broadcasts (applyDelta). Rendering only; ownership/economy are server-authoritative.
 */
import * as THREE from "three";
import { pointInBeacon, tierDef, type Beacon } from "@hyperlight/shared";
import type { StarSystem } from "./starsystem.js";

const OWNER_COLOR = 0x2fd8ff; // cyan — your claims
const OTHER_COLOR = 0xffa53a; // amber — someone else's

/** Boundary wall height (m) and the proximity band over which it fades in. */
const BOUNDARY_H = 10;
const BOUNDARY_FADE_M = 28;
const BOUNDARY_MAX_OPACITY = 0.2;

interface Marker {
  beacon: Beacon;
  planetId: string;
  group: THREE.Group;
  mine: boolean;
  /** Per-marker boundary material — opacity animated by proximity. */
  boundMat: THREE.MeshBasicMaterial | null;
}

export class HomesteadMarkers {
  private markers = new Map<string, Marker>();
  private pulse = 0;
  /** Beacon-placement mode: EVERY claim's boundary shows (avoid overlaps);
   *  otherwise only the owner sees their own walls. */
  showAllBoundaries = false;

  constructor(private system: StarSystem, private myUid: string | null = null) {}

  /** All beacons on a planet (client-side canClaim mirror for the placement ghost). */
  beaconsOn(planetId: string): Beacon[] {
    const out: Beacon[] = [];
    for (const m of this.markers.values()) if (m.planetId === planetId) out.push(m.beacon);
    return out;
  }

  /** How many beacons are currently marked (probe/debug readback). */
  get count(): number {
    return this.markers.size;
  }

  /** Marked beacon ids (probe readback — e.g. the fresh claim to build in). */
  debugIds(): string[] {
    return [...this.markers.keys()];
  }

  setSelf(uid: string | null): void {
    this.myUid = uid;
  }

  /** The beacon whose claim sphere contains planet-local `p` on `planetId`
   *  (build-mode "which claim am I standing in"), or null. */
  beaconAt(planetId: string, p: [number, number, number]): Beacon | null {
    for (const m of this.markers.values()) {
      if (m.planetId !== planetId) continue;
      if (pointInBeacon(m.beacon, p)) return m.beacon;
    }
    return null;
  }

  /** Replace the whole set from an authoritative hsState snapshot. */
  setBeacons(beacons: Beacon[]): void {
    const keep = new Set(beacons.map((b) => b.id));
    for (const id of [...this.markers.keys()]) if (!keep.has(id)) this.remove(id);
    for (const b of beacons) this.addOrUpdate(b);
  }

  /** Apply a live hsDelta (add/update/remove). */
  applyDelta(op: "add" | "update" | "remove", beacon: Beacon | null, beaconId?: string): void {
    if (op === "remove") {
      if (beaconId) this.remove(beaconId);
    } else if (beacon) {
      this.addOrUpdate(beacon);
    }
  }

  private addOrUpdate(beacon: Beacon): void {
    const existing = this.markers.get(beacon.id);
    // Only rebuild geometry when size/place actually changed.
    if (existing
      && existing.beacon.radius === beacon.radius
      && existing.beacon.tier === beacon.tier
      && existing.planetId === beacon.planetId
      && existing.beacon.anchor[0] === beacon.anchor[0]
      && existing.beacon.anchor[1] === beacon.anchor[1]
      && existing.beacon.anchor[2] === beacon.anchor[2]) {
      existing.beacon = beacon;
      return;
    }
    if (existing) this.remove(beacon.id);
    const planet = this.system.planets.find((p) => p.def.id === beacon.planetId);
    if (!planet) return; // planet not resident (different system / not loaded) — skip
    const mine = this.myUid !== null && beacon.ownerUid === this.myUid;
    const group = this.buildMarker(beacon, mine);
    planet.group.add(group);
    const boundMat = (group.userData as { boundMat?: THREE.MeshBasicMaterial }).boundMat ?? null;
    this.markers.set(beacon.id, { beacon, planetId: beacon.planetId, group, mine, boundMat });
  }

  private remove(id: string): void {
    const m = this.markers.get(id);
    if (!m) return;
    m.group.parent?.remove(m.group);
    disposeGroup(m.group);
    this.markers.delete(id);
  }

  private buildMarker(beacon: Beacon, mine: boolean): THREE.Group {
    const color = mine ? OWNER_COLOR : OTHER_COLOR;
    const group = new THREE.Group();
    const anchor = new THREE.Vector3(beacon.anchor[0], beacon.anchor[1], beacon.anchor[2]);
    group.position.copy(anchor);
    // Stand up along the local surface normal (anchor direction from planet centre).
    const up = anchor.clone().normalize();
    group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);

    const postMat = new THREE.MeshLambertMaterial({ color: 0x3a4453 });
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.22, 2.4, 0.22), postMat);
    post.position.y = 1.2;
    group.add(post);

    const glowMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 });
    const orb = new THREE.Mesh(new THREE.SphereGeometry(0.34, 12, 10), glowMat);
    orb.position.y = 2.5;
    orb.name = "hs-orb";
    group.add(orb);

    // Claim boundary: a transparent open cylinder WALL at the claim radius —
    // invisible until you near the edge (opacity driven in update()), and only
    // for the owner unless beacon-placement mode shows everyone's.
    const r = Math.max(tierDef(beacon.tier).radius, beacon.radius);
    const boundMat = new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: 0, side: THREE.DoubleSide,
      depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const wall = new THREE.Mesh(
      new THREE.CylinderGeometry(r, r, BOUNDARY_H, 64, 1, true), boundMat);
    wall.position.y = BOUNDARY_H / 2 - 2; // sink the base for terrain variance
    wall.name = "hs-bound";
    group.add(wall);
    (group as THREE.Group & { userData: { boundMat?: THREE.MeshBasicMaterial } })
      .userData.boundMat = boundMat;
    return group;
  }

  /** Gentle glow pulse + boundary proximity fade. `focusFor` resolves the
   *  camera into a planet's RENDER-local frame (the staged-bake driver's). */
  update(dt: number, focusFor?: (planetId: string) => [number, number, number] | null): void {
    this.pulse += dt;
    const s = 1 + 0.12 * Math.sin(this.pulse * 3);
    for (const m of this.markers.values()) {
      const orb = m.group.getObjectByName("hs-orb");
      if (orb) orb.scale.setScalar(s);
      if (!m.boundMat) continue;
      // Visibility policy: owner-only, unless placement mode reveals all.
      if (!m.mine && !this.showAllBoundaries) { m.boundMat.opacity = 0; continue; }
      const focus = focusFor?.(m.planetId) ?? null;
      if (!focus) { m.boundMat.opacity = 0; continue; }
      const dx = focus[0] - m.beacon.anchor[0];
      const dy = focus[1] - m.beacon.anchor[1];
      const dz = focus[2] - m.beacon.anchor[2];
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      // Fade in as the camera nears the WALL itself (inside or outside).
      const toBoundary = Math.abs(dist - m.beacon.radius);
      const t = Math.max(0, 1 - toBoundary / BOUNDARY_FADE_M);
      // Placement mode holds a floor so every claim reads at a glance.
      const floor = this.showAllBoundaries && dist < m.beacon.radius + 400 ? 0.06 : 0;
      m.boundMat.opacity = Math.max(floor, BOUNDARY_MAX_OPACITY * t * t);
    }
  }

  dispose(): void {
    for (const id of [...this.markers.keys()]) this.remove(id);
  }
}

function disposeGroup(group: THREE.Group): void {
  group.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.geometry.dispose();
      const mat = o.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat.dispose();
    }
  });
}
